import { ChangeDetectionStrategy, Component, OnInit, Inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize, flatMap, Subject, takeUntil } from 'rxjs';
import { PartnerSubscriptionTypeMappingDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/partner-subscription-type-mapping-dto';
import { UpdatePartnerSubscriptionTypeMappingRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/update-partner-subscription-type-mapping-request';
import { UpdatePartnerSubscriptionTypeMappingResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/update-partner-subscription-type-mapping-response';
import { PartnerSubscriptionTypeService } from 'src/app/api/services/partner-subscription-type.service';
import { PartnerAbonnementerEditDialogData } from 'src/app/components/aftale/models';
import { ConfirmDialogComponent } from 'src/app/components/shared/confirm-dialog/confirm-dialog.component';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-partnerabonnementer-edit-dialog',
  templateUrl: './partnerabonnementer-edit-dialog.component.html',
  styleUrls: ['./partnerabonnementer-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PartnerabonnementerEditDialogComponent implements OnInit {
  form: UntypedFormGroup;
  partnerSubscriptionTypeMapping: PartnerSubscriptionTypeMappingDto;
  vareInfoCollected: boolean = false;
  noVareInfoInG4: boolean = false;
  unsubscribe$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private partnerSubscriptionTypeService: PartnerSubscriptionTypeService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<PartnerabonnementerEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PartnerAbonnementerEditDialogData,
  ) {
    this.partnerSubscriptionTypeMapping = data.partnerSubscriptionTypeMapping;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      description: [
        this.partnerSubscriptionTypeMapping.partnerSubscriptionTypeId +
          ' - ' +
          this.partnerSubscriptionTypeMapping.partnerName +
          '(' +
          this.partnerSubscriptionTypeMapping.subscriptionTypeName +
          ')',
      ],
      newPartnerPrice: ['', [Validators.min(1), Validators.required]],
      partnerPrice: [this.partnerSubscriptionTypeMapping.partnerPrice],
      previousPartnerPrice: [this.partnerSubscriptionTypeMapping.previousPartnerPrice],
    });
  }

  save() {
    if (
      this.partnerSubscriptionTypeMapping.previousPartnerPrice === null ||
      this.partnerSubscriptionTypeMapping.previousPartnerPrice === 0
    ) {
      const request: UpdatePartnerSubscriptionTypeMappingRequest = {
        id: this.partnerSubscriptionTypeMapping.id,
        newPartnerPrice: this.form.get('newPartnerPrice').value,
        oldPartnerPrice: this.partnerSubscriptionTypeMapping.partnerPrice,
        updateOldPartnerPrice: true,
      };

      this.partnerSubscriptionTypeService
        .updatePartnerSubscriptionTypeMapping({ caller: EGIS_SERVICE_CALLER, body: request })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          (result: UpdatePartnerSubscriptionTypeMappingResponse) => {
            this.dialogRef.close(result);
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
      return;
    }

    let dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Opdatering af tidligere pris',
        body:
          'Vil du opdatere den tidligere pris fra ' +
          this.partnerSubscriptionTypeMapping.previousPartnerPrice +
          ' til ' +
          this.partnerSubscriptionTypeMapping.partnerPrice +
          '?' +
          '\n\n' +
          'Hvis du vælger "Ja" vil vi fremover acceptere ' +
          this.form.get('newPartnerPrice').value +
          ' og ' +
          this.partnerSubscriptionTypeMapping.partnerPrice +
          ' som pris for dette abonnement.' +
          '\n\n' +
          'Hvis du vælger "Nej" vil vi fremover acceptere ' +
          this.form.get('newPartnerPrice').value +
          ' og ' +
          this.partnerSubscriptionTypeMapping.previousPartnerPrice +
          ' som pris for dette abonnement.',
        confirmButtonText: 'Ja',
        cancelButtonText: 'Nej',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        finalize(() => {
          this.loader.show();
        }),
      )
      .subscribe((confirm) => {
        const request: UpdatePartnerSubscriptionTypeMappingRequest = {
          id: this.partnerSubscriptionTypeMapping.id,
          newPartnerPrice: this.form.get('newPartnerPrice').value,
          oldPartnerPrice: this.partnerSubscriptionTypeMapping.partnerPrice,
          updateOldPartnerPrice: confirm ? true : false,
        };

        this.partnerSubscriptionTypeService
          .updatePartnerSubscriptionTypeMapping({ caller: EGIS_SERVICE_CALLER, body: request })
          .pipe(
            finalize(() => {
              this.loader.hide();
            }),
            takeUntil(this.unsubscribe$),
          )
          .subscribe(
            (result: UpdatePartnerSubscriptionTypeMappingResponse) => {
              this.dialogRef.close(result);
            },
            (error) => {
              this.errorHandler.HandleError(error);
            },
          );
      });
  }
}
